import { IntegrationStatus } from '../../shared/interfaces/integration';
import { GenericStatus, GenericStatusColorType } from '../../shared/interfaces/generic-status';

export const PIPELINE_RENDERED_STATUSES = {
  FAILED: 'Failed',
  PAUSED: 'Paused',
  DISABLING: 'Disabling...',
  INITIALIZING: 'Initialising...',
  DISABLED: 'Disabled',
  ENABLING: 'Enabling...',
  ENABLED: 'Enabled',
  DRAFT: 'Draft',
  RESYNCING: 'Resyncing',
  DELETING: 'Deleting...',
  DELETED: 'Deleted'
};

export const PIPELINE_FILTER_STATUSES = Object.entries(PIPELINE_RENDERED_STATUSES)
  .filter(([k]) => !['DRAFT', 'DELETING', 'DELETED'].includes(k))
  .map(([k, v]) => {
    return {
      label: v,
      value: k
    };
  });

export const PIPELINE_RENDERED_STATUSES_VALUES = {
  [IntegrationStatus.DISABLE_FAILED]: 'FAILED',
  [IntegrationStatus.ENABLE_FAILED]: 'FAILED',
  [IntegrationStatus.SYNC_TASK_FLOW_FAILED]: 'FAILED',
  [IntegrationStatus.DELETE_FAILED]: 'FAILED',
  [IntegrationStatus.PAUSED]: 'PAUSED',
  [IntegrationStatus.DISABLING]: 'DISABLING',
  [IntegrationStatus.ENABLING]: 'ENABLING',
  [IntegrationStatus.STREAMING]: 'ENABLED',
  [IntegrationStatus.INITIALIZED]: 'ENABLED',
  [IntegrationStatus.INITIALIZING]: 'INITIALIZING',
  [IntegrationStatus.INITIALIZE_FAILED]: 'FAILED',
  [IntegrationStatus.DISABLED]: 'DISABLED',
  [IntegrationStatus.CONFIGURED_OBJECT]: 'DRAFT',
  [IntegrationStatus.CONFIGURED_PIPELINE]: 'DRAFT',
  [IntegrationStatus.CREATED_DESTINATION]: 'DRAFT',
  [IntegrationStatus.CREATED_SOURCE]: 'DRAFT',
  [IntegrationStatus.CREATED_CATALOG]: 'DRAFT',
  [IntegrationStatus.CREATED]: 'DRAFT',
  [IntegrationStatus.RESTARTING]: 'RESYNCING',
  [IntegrationStatus.RESTART_FAILED]: 'FAILED',
  [IntegrationStatus.DELETING]: 'DELETING',
  [IntegrationStatus.DELETED]: 'DELETED'
};

export const PIPELINE_STATUS_DATA_MAP: {
  [key in IntegrationStatus]: GenericStatus<IntegrationStatus>;
} = {
  [IntegrationStatus.DISABLE_FAILED]: {
    value: IntegrationStatus.DISABLE_FAILED,
    displayName: PIPELINE_RENDERED_STATUSES.FAILED,
    colorType: GenericStatusColorType.ERROR
  },
  [IntegrationStatus.ENABLE_FAILED]: {
    value: IntegrationStatus.ENABLE_FAILED,
    displayName: PIPELINE_RENDERED_STATUSES.FAILED,
    colorType: GenericStatusColorType.ERROR
  },
  [IntegrationStatus.SYNC_TASK_FLOW_FAILED]: {
    value: IntegrationStatus.SYNC_TASK_FLOW_FAILED,
    displayName: PIPELINE_RENDERED_STATUSES.FAILED,
    colorType: GenericStatusColorType.ERROR
  },
  [IntegrationStatus.DELETE_FAILED]: {
    value: IntegrationStatus.DELETE_FAILED,
    displayName: PIPELINE_RENDERED_STATUSES.FAILED,
    colorType: GenericStatusColorType.ERROR
  },
  [IntegrationStatus.PAUSED]: {
    value: IntegrationStatus.PAUSED,
    displayName: PIPELINE_RENDERED_STATUSES.PAUSED,
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.DISABLED]: {
    value: IntegrationStatus.DISABLED,
    displayName: PIPELINE_RENDERED_STATUSES.DISABLED,
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.DISABLING]: {
    value: IntegrationStatus.DISABLED,
    displayName: PIPELINE_RENDERED_STATUSES.DISABLING,
    icon: 'testing',
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.ENABLING]: {
    value: IntegrationStatus.ENABLING,
    displayName: PIPELINE_RENDERED_STATUSES.ENABLING,
    icon: 'testing',
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.STREAMING]: {
    value: IntegrationStatus.STREAMING,
    displayName: PIPELINE_RENDERED_STATUSES.ENABLED,
    colorType: GenericStatusColorType.SUCCESS
  },
  [IntegrationStatus.INITIALIZED]: {
    value: IntegrationStatus.INITIALIZED,
    displayName: PIPELINE_RENDERED_STATUSES.ENABLED,
    colorType: GenericStatusColorType.SUCCESS
  },
  [IntegrationStatus.INITIALIZE_FAILED]: {
    value: IntegrationStatus.INITIALIZE_FAILED,
    displayName: PIPELINE_RENDERED_STATUSES.FAILED,
    colorType: GenericStatusColorType.ERROR
  },
  [IntegrationStatus.INITIALIZING]: {
    value: IntegrationStatus.INITIALIZING,
    displayName: PIPELINE_RENDERED_STATUSES.INITIALIZING,
    colorType: GenericStatusColorType.DEFAULT
  },
  [IntegrationStatus.CONFIGURED_PIPELINE]: {
    value: IntegrationStatus.CONFIGURED_PIPELINE,
    displayName: PIPELINE_RENDERED_STATUSES.DRAFT,
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.CONFIGURED_OBJECT]: {
    value: IntegrationStatus.CONFIGURED_OBJECT,
    displayName: PIPELINE_RENDERED_STATUSES.DRAFT,
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.CREATED_DESTINATION]: {
    value: IntegrationStatus.CREATED_DESTINATION,
    displayName: PIPELINE_RENDERED_STATUSES.DRAFT,
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.CREATED_CATALOG]: {
    value: IntegrationStatus.CREATED_CATALOG,
    displayName: PIPELINE_RENDERED_STATUSES.DRAFT,
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.CREATED_SOURCE]: {
    value: IntegrationStatus.CREATED_SOURCE,
    displayName: PIPELINE_RENDERED_STATUSES.DRAFT,
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.CREATED]: {
    value: IntegrationStatus.CREATED,
    displayName: PIPELINE_RENDERED_STATUSES.DRAFT,
    colorType: GenericStatusColorType.DISABLED
  },
  [IntegrationStatus.RESTART_FAILED]: {
    value: IntegrationStatus.RESTART_FAILED,
    displayName: PIPELINE_RENDERED_STATUSES.FAILED,
    colorType: GenericStatusColorType.ERROR
  },
  [IntegrationStatus.RESTARTING]: {
    value: IntegrationStatus.RESTARTING,
    displayName: PIPELINE_RENDERED_STATUSES.RESYNCING,
    colorType: GenericStatusColorType.INFO
  },
  [IntegrationStatus.DELETING]: {
    value: IntegrationStatus.DELETING,
    displayName: PIPELINE_RENDERED_STATUSES.DELETING,
    colorType: GenericStatusColorType.ERROR
  },
  [IntegrationStatus.DELETED]: {
    value: IntegrationStatus.DELETED,
    displayName: PIPELINE_RENDERED_STATUSES.DELETED,
    colorType: GenericStatusColorType.ERROR
  }
};
